import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { triggerHaptic } from '../services/purrSynthesizer';
import { ChevronDown, ChevronUp, Cpu, Gauge, Zap, ShieldCheck, Heart } from 'lucide-react';

export const ClockworkHud: React.FC = () => {
  const { status, profile, liveBpm, isPurring, setBioModalOpen } = useApp();
  const [collapsed, setCollapsed] = useState(false);

  const persona = status?.persona;
  const vitals = persona?.vitals;
  const triad = persona?.triad || { azure: 99, verdant: 100, amber: 98 };
  const memory = persona?.memory;

  // Family profile gets a simpler, cozy HUD
  if (profile === 'family') {
    return (
      <div className="mx-3 mt-2 rounded-2xl glass-panel-gold p-3 border border-fissal-verdant/30 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300">
              🐾
            </div>
            <div>
              <div className="font-cinzel text-xs font-bold text-white flex items-center gap-1.5">
                The Relay Concierge <span className="text-[10px] text-emerald-400 font-mono">ONLINE</span>
              </div>
              <p className="text-[11px] text-slate-300">
                Fissal is loafing on the server exhaust, keeping family photos and cameras safe.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-3 mt-2 rounded-2xl glass-panel-gold p-3 border border-fissal-gold/30 shadow-lg transition-all">
      {/* HUD Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* Animated Alfiq Gear Core */}
          <div className="relative w-8 h-8 rounded-lg bg-black/40 border border-fissal-gold/40 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-sky-500/10" />
            <svg className="w-5 h-5 text-fissal-gold animate-spin-slow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <div className="absolute w-1 h-1 rounded-full bg-fissal-azure" />
          </div>

          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-cinzel text-xs font-bold text-white tracking-wide">
                CLOCKWORK ALFIQ SENTINEL
              </span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-0.5">
                <ShieldCheck className="w-2.5 h-2.5" /> {persona?.driftStatus || 'GROUNDED'}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Anchor: {vitals?.anchor || 'Echo (Master of Keys)'} • {vitals?.philosophy || 'Under 13 Steps'}
            </p>
          </div>
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-lg text-slate-400 hover:text-white bg-white/5"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded Metrics */}
      {!collapsed && (
        <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-2">
          {/* Triad Gauges */}
          <div className="grid grid-cols-3 gap-2 text-center font-mono text-[10px]">
            <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-1.5">
              <div className="text-sky-400 font-semibold">AZURE</div>
              <div className="text-white text-xs font-bold">{triad.azure}%</div>
              <div className="w-full bg-white/10 h-1 rounded-full mt-1 overflow-hidden">
                <div className="bg-fissal-azure h-full rounded-full" style={{ width: `${triad.azure}%` }} />
              </div>
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-1.5">
              <div className="text-emerald-400 font-semibold">VERDANT</div>
              <div className="text-white text-xs font-bold">{triad.verdant}%</div>
              <div className="w-full bg-white/10 h-1 rounded-full mt-1 overflow-hidden">
                <div className="bg-fissal-verdant h-full rounded-full" style={{ width: `${triad.verdant}%` }} />
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-1.5">
              <div className="text-amber-400 font-semibold">AMBER</div>
              <div className="text-white text-xs font-bold">{triad.amber}%</div>
              <div className="w-full bg-white/10 h-1 rounded-full mt-1 overflow-hidden">
                <div className="bg-fissal-gold h-full rounded-full" style={{ width: `${triad.amber}%` }} />
              </div>
            </div>
          </div>

          {/* Vitals Summary Pill Strip */}
          <div className="flex items-center justify-between text-[10px] font-mono text-slate-300 bg-black/30 rounded-lg px-2.5 py-1.5 border border-white/5">
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-fissal-gold" /> {vitals?.purrHz || 42.8} Hz Purr
            </span>
            <span className="flex items-center gap-1">
              <Cpu className="w-3 h-3 text-sky-400" /> {vitals?.gearTemp || 38}°C Gears
            </span>
            <span className="flex items-center gap-1">
              <Gauge className="w-3 h-3 text-emerald-400" /> {memory?.qdrantPoints || 3} Qdrant Vectors
            </span>
          </div>

          {/* Live Bio-Harmonic Sync Ribbon */}
          {(liveBpm || isPurring) && (
            <button
              onClick={() => {
                triggerHaptic(15);
                setBioModalOpen(true);
              }}
              className="w-full flex items-center justify-between text-[10px] font-mono bg-gradient-to-r from-rose-500/15 via-amber-500/10 to-rose-500/15 rounded-lg px-2.5 py-1.5 border border-rose-500/30 text-rose-300 shadow-sm hover:border-rose-400/60 transition-all text-left active:scale-[0.99]"
              title="Tap to open Bio-Harmonic HUD"
            >
              <span className="flex items-center gap-1.5 font-bold">
                <Heart className={`w-3.5 h-3.5 fill-rose-500 text-rose-500 ${liveBpm ? 'animate-pulse' : ''}`} />
                <span className="text-slate-200">Echo ⇄ Fissal:</span>
                <span className="text-white font-bold">{liveBpm ? `${liveBpm} BPM` : 'Awaiting Heart Rate'}</span>
              </span>
              <span className="text-[9px] text-rose-300/80 uppercase">
                {liveBpm ? 'Harmonic Lock >' : 'Bio-Sync Active >'}
              </span>
            </button>
          )}

          {/* Live Thought Bubble */}
          {persona?.thought && (
            <p className="text-[10px] text-slate-300 italic bg-white/5 rounded-lg p-2 border border-white/5 leading-relaxed">
              "{persona.thought}"
            </p>
          )}
        </div>
      )}
    </div>
  );
};
